import {
    Add,
    ArchiveOutlined,
    Delete,
    Edit,
    HistoryOutlined,
    Link as LinkIcon,
    Search,
    UnarchiveOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box, Breadcrumbs,
    Button, Chip,
    IconButton, MenuItem,
    Paper, Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, TableSortLabel, TextField, Tooltip,
    Typography, useMediaQuery, useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from "../../components/DidNotLoad";
import { ColorSwatchLabel } from '../../components/HtmlColorField';
import Loading from '../../components/Loading';
import CustomPagination from "../../components/Pagination";
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents, formatDate, presentUuid } from '../../helpers/formatters';
import FilamentLowStockPanel from '../../components/materials/FilamentLowStockPanel';
import FilamentStockBar from '../../components/materials/FilamentStockBar';
import FilamentUsageDialog from '../../components/materials/FilamentUsageDialog';
import FilamentsInUseTable from '../../components/materials/FilamentsInUseTable';
import AssignFilamentToPrintersDialog from '../../components/materials/AssignFilamentToPrintersDialog';
import ActionBubbleTooltip from '../../components/ActionBubbleTooltip';
import { formatGrams, DEFAULT_FILAMENT_SORT_BY, DEFAULT_FILAMENT_SORT_ORDER } from '../../helpers/filaments';

function ListMaterialsPage() {
    const [materials, setMaterials] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento
    const navigate = useNavigate(); // Usando useNavigate para redirecionamento
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const [activeSearchQuery, setActiveSearchQuery] = useState('');
    const [usageFilament, setUsageFilament] = useState(null);
    const [assignFilament, setAssignFilament] = useState(null);
    const [inUseMaterials, setInUseMaterials] = useState([]);
    const [printerMetaByUuid, setPrinterMetaByUuid] = useState({});
    // Carretel esgotado sai da lista ativa, já que com uso registrado ele não
    // pode mais ser excluído e acumularia para sempre.
    const [showArchived, setShowArchived] = useState(false);
    const [archivedTotal, setArchivedTotal] = useState(0);
    // Cada recarga da listagem também atualiza o indicador de estoque baixo.
    const [inventoryKey, setInventoryKey] = useState(0);
    const [sortBy, setSortBy] = useState(DEFAULT_FILAMENT_SORT_BY);
    const [sortOrder, setSortOrder] = useState(DEFAULT_FILAMENT_SORT_ORDER);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchMaterials = useCallback(() => {
        setIsLoading(true); // Indica que o carregamento está em andamento
        const stockParams = {
            page: currentPage,
            limit: itemsPerPage,
            q: activeSearchQuery,
            archived: showArchived ? 'archived' : '',
            sort_by: sortBy,
            sort_order: sortOrder,
        };
        if (!showArchived) {
            stockParams.assignment_scope = 'stock';
        }
        const stockRequest = api.get('filaments', { params: stockParams });
        const inUseRequest = showArchived
            ? Promise.resolve({ data: { materials: [] } })
            : api.get('filaments', {
                params: {
                    page: 1,
                    limit: 200,
                    assignment_scope: 'in_use',
                    sort_by: 'created_at',
                    sort_order: 'asc',
                },
            });
        const printersRequest = showArchived
            ? Promise.resolve({ data: { printers: [] } })
            : api.get('printers', { params: { page: 1, limit: 200 } });

        Promise.all([stockRequest, inUseRequest, printersRequest])
            .then(([stockResponse, inUseResponse, printersResponse]) => {
                setMaterials(stockResponse.data.materials);
                setTotalPages(stockResponse.data.total_pages);
                setArchivedTotal(stockResponse.data.archived_total || 0);
                setInUseMaterials(inUseResponse.data.materials || []);
                // API returns printers by created_at DESC; reverse → ASC registration order.
                const metaByUuid = {};
                [...(printersResponse.data.printers || [])].reverse().forEach((printer, index) => {
                    const id = printer.id || printer.uuid;
                    if (id) {
                        metaByUuid[id] = {
                            order: index,
                            brand: printer.brand || '',
                            name: printer.name || '',
                        };
                    }
                });
                setPrinterMetaByUuid(metaByUuid);
                setInventoryKey((key) => key + 1);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false); // Finaliza o carregamento
            });
    }, [currentPage, itemsPerPage, activeSearchQuery, showArchived, sortBy, sortOrder]);

    useListFetch(fetchMaterials, [currentPage, itemsPerPage, activeSearchQuery, showArchived, sortBy, sortOrder, fetchMaterials]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortOrder(field === DEFAULT_FILAMENT_SORT_BY ? DEFAULT_FILAMENT_SORT_ORDER : 'asc');
        }
        setCurrentPage(1);
    };

    const resetFilamentSort = () => {
        setSortBy(DEFAULT_FILAMENT_SORT_BY);
        setSortOrder(DEFAULT_FILAMENT_SORT_ORDER);
    };

    const handleSearch = () => {
        const nextQuery = searchTerm.trim();
        setCurrentPage(1);
        setActiveSearchQuery(nextQuery);
        setAppliedSearchTerm(Boolean(nextQuery));
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
        resetFilamentSort();
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setActiveSearchQuery('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
        resetFilamentSort();
    };

    const handleCreateMaterial = () => {
        navigate('/admin/register-material');  // Redireciona para a página de criação de material
    };

    const handleEditMaterial = (id) => {
        navigate(`/admin/edit-material/${id}`);  // Redireciona para a página de edição
    };

    const setArchived = (material, archived) => {
        api.post(`filaments/${material.id}/archive`, { archived })
            .then(() => {
                fetchMaterials();
            })
            .catch(() => {
                Swal.fire(
                    'Erro!',
                    archived
                        ? 'Houve um problema ao arquivar o filamento.'
                        : 'Houve um problema ao reativar o filamento.',
                    'error',
                );
            });
    };

    // O carretel com consumo registrado não pode ser excluído sem levar o
    // histórico das impressões junto, então o caminho é tirá-lo de circulação.
    const handleArchiveMaterial = (material) => {
        const usageText = material.usage_count === 1
            ? 'Ele já foi utilizado em 1 impressão'
            : `Ele já foi utilizado em ${material.usage_count} impressões`;

        Swal.fire({
            title: 'Arquivar filamento?',
            text: `${usageText}, então o cadastro precisa continuar existindo para preservar esse histórico. `
                + 'Arquivando, ele sai desta listagem e das opções ao registrar uma impressão, '
                + 'e você pode reativá-lo quando quiser.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Arquivar',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                setArchived(material, true);
            }
        });
    };

    const handleDeleteMaterial = (material) => {
        const materialId = material.id;

        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Este material será removido permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete, // Impede que o SweetAlert seja fechado durante o carregamento
            preConfirm: () => {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`filaments/${materialId}`)
                        .then(() => {
                            setLoadingDelete(false);
                            fetchMaterials();
                            Swal.fire('Removido!', 'O material foi removido com sucesso.', 'success');
                        })
                        .catch(error => {
                            setLoadingDelete(false);
                            console.error('Erro ao remover o material:', error);
                            const blockedMessage = error?.response?.status === 409
                                ? error.response.data?.error
                                : '';
                            Swal.fire(
                                blockedMessage ? 'Não é possível remover' : 'Erro!',
                                blockedMessage || 'Houve um problema ao remover o material.',
                                blockedMessage ? 'info' : 'error',
                            );
                        });
                }
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Filamentos</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                {/* Título */}
                <Typography variant="h4" gutterBottom>
                    Filamentos
                </Typography>

                {/* Campo de busca + botões */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto'
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar filamento..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                </Box>

                {/* Botão de venda */}
                <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={showArchived ? <UnarchiveOutlined /> : <ArchiveOutlined />}
                        onClick={() => {
                            setShowArchived((prev) => !prev);
                            setCurrentPage(1);
                            resetFilamentSort();
                        }}
                        fullWidth={isMobile}
                    >
                        {showArchived
                            ? 'Ver ativos'
                            : `Ver arquivados${archivedTotal > 0 ? ` (${archivedTotal})` : ''}`}
                    </Button>
                    <PageTutorialButtons
                        pageKey="filaments"
                        fullWidth={isMobile}
                    />
                    <QuotaLimitedButton
                        quotaKey="filaments"
                        variant="contained"
                        color="primary"
                        onClick={handleCreateMaterial}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Filamento
                    </QuotaLimitedButton>
                </Box>
            </Box>
            {!showArchived && (
                <FilamentLowStockPanel
                    reloadKey={inventoryKey}
                    onSelect={(filament) => handleEditMaterial(filament.id || filament.uuid)}
                />
            )}
            {!showArchived && (
                <FilamentsInUseTable
                    materials={inUseMaterials}
                    printerMetaByUuid={printerMetaByUuid}
                    onChanged={fetchMaterials}
                    onEdit={handleEditMaterial}
                    onViewUsage={setUsageFilament}
                    onArchive={handleArchiveMaterial}
                    onDelete={handleDeleteMaterial}
                />
            )}
            {showArchived && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Estes carretéis estão fora de circulação: não aparecem na listagem principal nem nas opções
                    ao registrar uma impressão, mas o histórico de consumo deles continua preservado.
                    Reative qualquer um se ele voltar a ser usado.
                </Alert>
            )}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                {showArchived ? 'Filamentos arquivados' : 'Filamentos no estoque'}
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'type'}
                                    direction={sortBy === 'type' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('type')}
                                >
                                    Tipo
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'brand'}
                                    direction={sortBy === 'brand' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('brand')}
                                >
                                    Marca
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Cor</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Carretel</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>
                                <TableSortLabel
                                    active={sortBy === 'current_weight'}
                                    direction={sortBy === 'current_weight' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('current_weight')}
                                >
                                    Saldo
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'usage_count'}
                                    direction={sortBy === 'usage_count' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('usage_count')}
                                >
                                    Impressões
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Custo por KG</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'created_at'}
                                    direction={sortBy === 'created_at' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('created_at')}
                                >
                                    Cadastrado em
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(!Array.isArray(materials) || materials.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={10}>
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        {showArchived
                                            ? 'Nenhum filamento arquivado. Carretéis são arquivados automaticamente ao zerar o saldo.'
                                            : 'Nenhum filamento no estoque.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {Array.isArray(materials) && materials.length > 0 && materials.map((material) => (
                            <TableRow key={material.id}>
                                <TableCell>{presentUuid(material.id)}</TableCell>
                                <TableCell>{material.type}</TableCell>
                                <TableCell>{material.brand}</TableCell>
                                <TableCell>
                                    <ColorSwatchLabel
                                        color={material.color}
                                        label={material.color_label}
                                    />
                                </TableCell>
                                <TableCell>{formatGrams(material.spool_weight)}</TableCell>
                                <TableCell>
                                    <FilamentStockBar
                                        currentWeight={material.current_weight}
                                        spoolWeight={material.spool_weight}
                                    />
                                </TableCell>
                                <TableCell>
                                    {(material.usage_count || 0) > 0 ? (
                                        <Chip
                                            label={material.usage_count}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setUsageFilament(material)}
                                        />
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                    )}
                                </TableCell>
                                <TableCell>{formatCurrencyInCents(material.cost_per_kilogram)}</TableCell>
                                <TableCell>{formatDate(material.created_at)}</TableCell>
                                <TableCell>
                                    {!material.archived_at && (
                                        <ActionBubbleTooltip title="Vincular este filamento a uma ou mais impressoras">
                                            <IconButton
                                                onClick={() => setAssignFilament(material)}
                                                aria-label="vincular a impressora"
                                                sx={{ color: 'grey.500' }}
                                            >
                                                <LinkIcon />
                                            </IconButton>
                                        </ActionBubbleTooltip>
                                    )}
                                    <Tooltip title="Histórico de uso">
                                        <IconButton
                                            onClick={() => setUsageFilament(material)}
                                            aria-label="histórico de uso"
                                        >
                                            <HistoryOutlined />
                                        </IconButton>
                                    </Tooltip>
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleEditMaterial(material.id)}
                                    >
                                        <Edit />
                                    </IconButton>
                                    {material.archived_at ? (
                                        <Tooltip title="Reativar filamento">
                                            <IconButton
                                                color="primary"
                                                onClick={() => setArchived(material, false)}
                                                aria-label="reativar filamento"
                                            >
                                                <UnarchiveOutlined />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (material.usage_count || 0) > 0 ? (
                                        <Tooltip title="Arquivar filamento">
                                            <IconButton
                                                color="warning"
                                                onClick={() => handleArchiveMaterial(material)}
                                                aria-label="arquivar filamento"
                                            >
                                                <ArchiveOutlined />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Remover">
                                            <IconButton
                                                color="error"
                                                onClick={() => handleDeleteMaterial(material)}
                                                aria-label="remover material"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2, // Espaçamento entre o Select e a paginação
                    mt: 3, // Margem superior para afastar do conteúdo acima
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
            <FilamentUsageDialog
                open={Boolean(usageFilament)}
                filament={usageFilament}
                onClose={() => setUsageFilament(null)}
            />
            <AssignFilamentToPrintersDialog
                open={Boolean(assignFilament)}
                filament={assignFilament}
                onClose={() => setAssignFilament(null)}
                onAssigned={fetchMaterials}
            />
        </div>
    );
}

export default ListMaterialsPage;
